import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  maintenancePartsOrderAsk,
  PURCHASE_REQUEST_STATUS_LABELS,
  PURCHASE_REQUEST_STATUSES,
  type PurchaseRequestStatus,
} from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { OrderWithNancy } from "@/components/app/clinical/order-with-nancy";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus } from "@/lib/icons";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { orpc } from "@/lib/orpc";
import { guardFeature } from "@/lib/policy/route-guard";
import { optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { usePaging } from "@/routes/_authenticated/menus/-lib/use-paging";
import { PurchaseRequestForm } from "./-components/purchase-request-form";
import { PurchasesTable } from "./-components/purchases-table";
import { useMaintenanceBuilding } from "./-lib/use-maintenance-building";

type StatusFilter = PurchaseRequestStatus | "open-any" | "";

/** The address leaves out the default "open-any" and spells "every request" as "all". */
const STATUS_PARAMS = ["all", ...PURCHASE_REQUEST_STATUSES] as const;

export const Route = createFileRoute("/_authenticated/_maintenance/maintenance/purchases")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { status?: (typeof STATUS_PARAMS)[number] } => ({
    status: optionalOneOf(STATUS_PARAMS, search.status),
  }),
  beforeLoad: ({ context }) => guardFeature(context.me, "maintenance-shop"),
  component: PurchasesPage,
});

/** The parts to buy: asked for here or by David, approved by whoever may spend, then sent to
    Nancy to shop and received into the shop. */
function PurchasesPage() {
  const { facilityId, canManage, canApprove } = useMaintenanceBuilding();
  const navigate = useNavigate({ from: Route.fullPath });
  const statusParam = Route.useSearch().status;
  const status: StatusFilter =
    statusParam === undefined ? "open-any" : statusParam === "all" ? "" : statusParam;
  const setStatus = (next: StatusFilter) =>
    void navigate({
      search: { status: next === "open-any" ? undefined : next === "" ? "all" : next },
      ...KEEP_PLACE,
    });
  const [asking, setAsking] = useState(false);
  const paging = usePaging(50);
  const requests = useQuery({
    ...orpc.agents.maintenance.purchases.list.queryOptions({
      input: {
        facilityId,
        limit: paging.size,
        offset: paging.offset,
        ...(status ? { status } : {}),
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const approved = useQuery(
    orpc.agents.maintenance.purchases.list.queryOptions({
      input: { facilityId, status: "approved", limit: 1, offset: 0 },
    }),
  );
  const approvedCount = approved.data?.total ?? 0;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <OptionSelect
          aria-label="Status"
          value={status}
          className="w-48"
          onChange={(event) => {
            setStatus(event.target.value as StatusFilter);
            paging.reset();
          }}
        >
          <SelectOption value="open-any">Open</SelectOption>
          <SelectOption value="">Every request</SelectOption>
          {PURCHASE_REQUEST_STATUSES.map((key) => (
            <SelectOption key={key} value={key}>
              {PURCHASE_REQUEST_STATUS_LABELS[key]}
            </SelectOption>
          ))}
        </OptionSelect>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canManage ? (
            <Dialog open={asking} onOpenChange={setAsking}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline">
                  <Plus className="size-3.5" />
                  Ask to buy
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Ask to buy a part</DialogTitle>
                  <DialogDescription>
                    It waits for someone who may approve the spend, then Nancy shops it.
                  </DialogDescription>
                </DialogHeader>
                <PurchaseRequestForm facilityId={facilityId} onSaved={() => setAsking(false)} />
              </DialogContent>
            </Dialog>
          ) : null}
          <OrderWithNancy
            facilityId={facilityId}
            askFor={(building) => maintenancePartsOrderAsk(building ?? requests.data?.building)}
            disabled={approved.isPending || approvedCount === 0}
          />
        </div>
      </div>
      {approvedCount > 0 ? (
        <p className="text-muted-foreground text-sm">
          {String(approvedCount)} approved and ready for Nancy to shop.
        </p>
      ) : null}

      {requests.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : requests.isError ? (
        <ReadFailed
          message="Couldn't load the parts to buy."
          error={requests.error}
          onRetry={() => void requests.refetch()}
        />
      ) : requests.data.rows.length === 0 ? (
        <EmptyState
          title={status === "open-any" ? "Nothing to buy" : "No request matches"}
          description="A part is asked for from a work order, from the shop when it runs low, or here. Once approved, Nancy shops it."
        />
      ) : (
        <PurchasesTable
          facilityId={facilityId}
          canManage={canManage}
          canApprove={canApprove}
          rows={requests.data.rows}
          paging={paging}
          total={requests.data.total}
          busy={requests.isPlaceholderData}
        />
      )}
    </section>
  );
}
